import type { FeatureKey } from '@condo/contracts';
import type { LucideIcon } from 'lucide-react';
import {
  Users,
  Wallet,
  Receipt,
  QrCode,
  Package,
  CalendarDays,
  Wrench,
  UserCog,
  FileClock,
  MessageCircle,
  Gavel,
  Store,
  Bot,
  TrendingDown,
  Calculator,
  AlertTriangle,
  FileText,
  CreditCard,
  Landmark,
  MessageSquare,
  FileCheck,
  PenTool,
  ShoppingCart,
  BarChart3,
} from 'lucide-react';

export type FlagGroup = 'modules' | 'ia' | 'integrations' | 'red';

export const GROUP_LABELS: Record<FlagGroup, string> = {
  modules: 'Módulos',
  ia: 'IA (add-ons)',
  integrations: 'Integraciones',
  red: 'Red entre condominios',
};

export const GROUP_DESCRIPTIONS: Record<FlagGroup, string> = {
  modules: 'Habilita o deshabilita los módulos principales del condominio.',
  ia: 'Asistentes inteligentes. Consumen créditos adicionales.',
  integrations: 'Conexiones con proveedores externos (pasarelas de pago, SII, etc.).',
  red: 'Funcionalidades que conectan al condominio con la red de la plataforma.',
};

export const FLAG_LABELS: Record<FeatureKey, string> = {
  // Módulos
  'modules.residentes': 'Residentes',
  'modules.finanzas': 'Finanzas',
  'modules.gastos_comunes': 'Gastos comunes',
  'modules.visitas': 'Visitas',
  'modules.paquetes': 'Paquetes',
  'modules.espacios_comunes': 'Espacios comunes',
  'modules.mantencion': 'Mantención',
  'modules.personal': 'Personal',
  'modules.bitacora': 'Bitácora',
  'modules.comunicaciones': 'Comunicaciones',
  'modules.asamblea': 'Asamblea',
  'modules.mercado': 'Mercado Vecinal',
  // IA
  'ia.chatbot': 'Chatbot',
  'ia.prediccion_morosidad': 'Predicción de morosidad',
  'ia.presupuesto': 'Asistente de presupuesto',
  'ia.anomalias': 'Detección de anomalías',
  'ia.resumen_acta': 'Resumen de actas',
  // Integraciones
  'integrations.webpay': 'WebPay Plus',
  'integrations.khipu': 'Khipu',
  'integrations.whatsapp': 'WhatsApp Business',
  'integrations.sii': 'SII (boletas/facturas)',
  'integrations.firma_avanzada': 'Firma electrónica avanzada',
  // Red
  'red.marketplace': 'Marketplace de proveedores',
  'red.compras_colectivas': 'Compras colectivas',
  'red.benchmarking': 'Benchmarking anónimo',
};

export const FLAG_DESCRIPTIONS: Record<FeatureKey, string> = {
  'modules.residentes': 'Directorio de residentes y propietarios, historial de unidades.',
  'modules.finanzas': 'Balance, ingresos y egresos, fondo de reserva, reportes.',
  'modules.gastos_comunes': 'Emisión mensual, prorrateo, certificados, multas y mora.',
  'modules.visitas': 'Registro de visitas, QR de acceso, pre-autorizaciones.',
  'modules.paquetes': 'Recepción, notificación y confirmación de retiro.',
  'modules.espacios_comunes': 'CRUD, calendario de disponibilidad, reservas.',
  'modules.mantencion': 'Solicitudes con adjuntos, seguimiento, proveedores.',
  'modules.personal': 'CRUD de conserjes y guardias, gestión de turnos.',
  'modules.bitacora': 'Libro digital de novedades del conserje.',
  'modules.comunicaciones': 'Circulares, notificaciones y avisos a residentes.',
  'modules.asamblea': 'Convocatorias, tabla de materias, votaciones, actas.',
  'modules.mercado': 'Avisos entre vecinos para vender o regalar ítems.',
  'ia.chatbot': 'Asistente conversacional para residentes.',
  'ia.prediccion_morosidad': 'Estima la probabilidad de morosidad por unidad.',
  'ia.presupuesto': 'Sugerencias para el presupuesto anual.',
  'ia.anomalias': 'Alertas automáticas sobre gastos fuera de patrón.',
  'ia.resumen_acta': 'Resúmenes ejecutivos de actas de asamblea.',
  'integrations.webpay': 'Pasarela de pago Transbank.',
  'integrations.khipu': 'Pagos vía transferencia bancaria.',
  'integrations.whatsapp': 'Envío de notificaciones por WhatsApp.',
  'integrations.sii': 'Emisión automática de boletas y facturas.',
  'integrations.firma_avanzada': 'Firma electrónica avanzada para actas.',
  'red.marketplace': 'Directorio de proveedores calificados por la red.',
  'red.compras_colectivas': 'Licitaciones y compras agrupadas entre condominios.',
  'red.benchmarking': 'Comparativa anónima de indicadores con otros condominios.',
};

export const FLAG_ICONS: Record<FeatureKey, LucideIcon> = {
  'modules.residentes': Users,
  'modules.finanzas': Wallet,
  'modules.gastos_comunes': Receipt,
  'modules.visitas': QrCode,
  'modules.paquetes': Package,
  'modules.espacios_comunes': CalendarDays,
  'modules.mantencion': Wrench,
  'modules.personal': UserCog,
  'modules.bitacora': FileClock,
  'modules.comunicaciones': MessageCircle,
  'modules.asamblea': Gavel,
  'modules.mercado': Store,
  'ia.chatbot': Bot,
  'ia.prediccion_morosidad': TrendingDown,
  'ia.presupuesto': Calculator,
  'ia.anomalias': AlertTriangle,
  'ia.resumen_acta': FileText,
  'integrations.webpay': CreditCard,
  'integrations.khipu': Landmark,
  'integrations.whatsapp': MessageSquare,
  'integrations.sii': FileCheck,
  'integrations.firma_avanzada': PenTool,
  'red.marketplace': Store,
  'red.compras_colectivas': ShoppingCart,
  'red.benchmarking': BarChart3,
};
